import './ApplyModal.scss';

import { Modal, Button, Form } from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import { api, setAuthToken } from '../../../utils/api';
import Brand from "../../../components/Brand";
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrentUser } from "../../../UserProvider";
import classNames from "classnames";
import z from 'zod';
import { useForm } from '../../../hooks/Form';
import { Link } from 'react-router-dom';
import SsoButtons from "../../LoginPage/SsoButtons";
import SpotifyConnect from '../../OnboardingPage/OnboardingSteps/SpotifyConnect';

const schema = z.object({
  email: z.string()
    .nonempty("Email is required")
    .email("Invalid email address")
});

function createInitialValues() {
  return {
    email: '',
  };
}

function createInitialErrors() {
  return {
    email: null,
  };
}

export default function ApplyModal({ show, handleClose, job }) {
  const { currentUser, refreshUser } = useCurrentUser();

  const [details, setDetails] = useState("");
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [applied, setApplied] = useState(false);

  const {
    isLoading,
    setIsLoading,
    errors,
    setErrors,
    setFieldError,
    values,
    setFieldValue
  } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });

  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [details]);

  /**
   * Load default application message
   * Only for logged-in users
   */
  useEffect(() => {
    if (!show) return;
    if (!currentUser) return;
    if (!job?.id) return;

    const applyOnLoad = async () => {
      try {
        setIsDetailsLoading(true);

        const { data } = await api.get(
          `/job_postings/apply/${job.id}`
        );
        const message = `${data}\n\nYou can find more about me at https://heypapaya.com/a/${currentUser.profile.display_name}`;
        setDetails(message);
      } catch (err) {
        console.error("Auto apply failed:", err);
      } finally {
        setIsDetailsLoading(false);
      }
    };

    applyOnLoad();
  }, [show, currentUser, job?.id]);

  /**
   * Reset modal state when opened
   */
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (show) {
      setDetails("");
      setErrors(createInitialErrors());
      setFieldValue("email", "");
      setIsLoading(false);
      setApplied(false);
    }
  }, [show]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleApply = async () => {
    // STEP 1 — ensure user exists / email captured
    if (!currentUser || !currentUser.registered) {
      try {
        setErrors(createInitialErrors());
        setIsLoading(true);

        const result = schema.safeParse(values);

        if (!result.success) {
          result.error.issues.forEach(err => {
            setFieldError(err.path[0], err.message);
          });
          return;
        }

        if (!currentUser) {
          const { data } = await api.post('/users', { email: values.email });
          setAuthToken(data);
        } else {
          await api.put('/users/me', { email: values.email });
        }

        await refreshUser();
      } catch (err) {
        if (err.response?.status === 409) {
          setFieldError('email', 'The email is already taken');
        } else {
          setFieldError('email', 'Something went wrong. Try again.');
        }
        return;
      } finally {
        setIsLoading(false);
      }
    }

    // STEP 2 — apply to job
    try {
      await api.post('/users_job_postings', {
        job_posting_id: job.id,
        details,
        applied: true
      });
      setApplied(true);
    } catch (err) {
      console.error("Failed to apply:", err);
    }
  };

  return (
    <Modal
      className="__apply-modal"
      show={show}
      onHide={handleClose}
      centered
    >
      <Modal.Header closeButton>
        <Brand.Logo height={46} className="brand__logo" />
      </Modal.Header>

      <Modal.Body>
        <Modal.Title className="modal-title">
          {
            (!currentUser || !currentUser.registered) ? 'Create a FREE account to apply' : 'Papaya Prepped Your Outreach'
          }
        </Modal.Title>

        {!applied && (
          <div className="modal-subtitle">
            Your application will be sent directly to the job poster, along with your profile. You’ll be notified as soon as they respond.
          </div>
        )}

        <Form>
          {/* Logged out controls */}
          {(!currentUser || !currentUser.registered) && (
            <div className="logged-out-controls">
              <div className="sso">
                <SsoButtons />
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>

                <Form.Control
                  value={values.email}
                  onChange={(e) =>
                    setFieldValue("email", e.target.value)
                  }
                />

                {errors.email && (
                  <p className="text-field__error">
                    {errors.email}
                  </p>
                )}
              </Form.Group>
            </div>
          )}

          {applied ? (
            <div className="apply-success">
              Your application has been sent!
            </div>
          ) : (
            <>
              {/* Textarea */}
              <Form.Group className="mb-3">
                {currentUser && isDetailsLoading ? (
                  <div className="textarea-loading">
                    Preparing Application
                  </div>
                ) : (
                  <Form.Control
                    className="details"
                    ref={textareaRef}
                    value={details}
                    as="textarea"
                    rows={1}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Anything you want to share in your application..."
                  />
                )}
              </Form.Group>
              <div className="cta">
                <Button
                  className={classNames(
                    "ppy-btn",
                    "ppy-btn-primary",
                    {
                      disabled:
                        isLoading ||
                        isDetailsLoading ||
                        (!currentUser && values.email === '')
                    }
                  )}
                  onClick={handleApply}
                  data-cta="final-apply"
                >
                  Apply <FontAwesomeIcon icon={faExternalLink} />
                </Button>
              </div>
            </>
          )}
        </Form>

        {currentUser && currentUser.profile && !currentUser.profile.completed && (
          <div className="complete-profile">
            <div className="modal-subtitle incomplete-profile">
              We noticed that your{" "}
              <Link
                to={`/a/${currentUser.profile.display_name}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                profile
              </Link>{" "}
              is incomplete. Click{" "}
              <Link
                to={`/a/${currentUser.profile.display_name}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </Link>{" "}
              to improve your chances.
            </div>

            <div className="modal-subtitle">
              Alternatively, connect Spotify to fill in the blanks:
            </div>

            <SpotifyConnect handleNext={handleClose} />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}